import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, HostListener, OnInit, ViewChild, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { IconModule } from '@library/ui/atoms/icon/icon.module';
import { TextModule } from '@library/ui/atoms/text/text.module';
import { PlaceInfos } from '@library/utils/interfaces/place-infos.interface';
import { PipesModule } from '@library/utils/pipes/pipes.module';
import { JawgMapsService } from '@library/utils/services/jawgmaps.service';
import { debounceTime, filter, map, mergeWith, Subject, switchMap } from 'rxjs';
import { EventManagerService, EventSource } from 'src/app/services/event-manager.service';

@Component({
  selector: 'app-search',
  imports: [IconModule, ReactiveFormsModule, CommonModule, TextModule, PipesModule],
  providers: [JawgMapsService],
  templateUrl: './search.component.html',
  styleUrl: './search.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchComponent implements OnInit {
  private readonly jawgMapsService = inject(JawgMapsService);
  private readonly eventManagerService = inject(EventManagerService);

  form = new FormGroup({
    searchText: new FormControl(''),
  });

  focus = new Subject<void>();
  focus$ = this.focus.asObservable().pipe(map(() => this.form.get('searchText')?.value));

  resetPlaces$ = new Subject<PlaceInfos[]>();
  places$ = this.form.get('searchText')?.valueChanges.pipe(
    mergeWith(this.focus$),
    filter((searchText) => searchText.length > 3),
    debounceTime(200),
    switchMap((searchText) => this.jawgMapsService.getPlacesByText(searchText)),
    mergeWith(this.resetPlaces$),
  );

  @ViewChild('input') input: ElementRef<HTMLInputElement>;

  ngOnInit(): void {
    this.eventManagerService.registerSource(EventSource.SEARCH, () => {
      this.resetPlaces();
    });
  }

  @HostListener('click')
  onClick(): void {
    this.eventManagerService.closeOtherSources(EventSource.SEARCH);
  }

  setPlace(place: PlaceInfos): void {
    this.form.get('searchText')?.setValue(place.address, { emitEvent: false });
    this.form.markAsPristine();
    this.resetPlaces();
    if (place.bbox) {
      window.map.fitBounds(place.bbox, { maxZoom: 13, essential: true, maxDuration: 1500 });
    } else {
      window.map.flyTo({ center: place.geometry.coordinates, zoom: 13 });
    }
  }

  resetPlaces(): void {
    this.resetPlaces$.next([]);
  }

  clear(): void {
    this.form.get('searchText')?.setValue('');
    this.form.markAsPristine();
    this.resetPlaces();
    this.input.nativeElement.focus();
  }
}
